import { useEffect } from 'react';
import { createSelector } from 'reselect';
import { DEFAULT_IMPACT_REPORT_DATE_SELECTION } from '@/impactReports/impactReportDateRange.utilities';
import { useRegisterPersistedStore } from '@/stores/PersistenceProvider';
import { type ActionsFactory, createStore, createStoreInstance } from '@seeqdev/utils';
import type { DateRangePreset, DateRangeSelection } from '@/datetime/relativeDateRange.types';
import type {
  ImpactReportCategory,
  ImpactReportColumnConfiguration,
  ImpactReportConfiguration,
  ImpactReportDashboardContent,
  ImpactReportFilterModel,
  ImpactReportLayout,
  ImpactReportUseCase,
  ImpactTotals,
  MapZoomSettings,
} from '@/impactReports/impactReport.types';
import type { ColumnDefinitionOutputV1 } from '@seeqdev/api/model/ColumnDefinitionOutputV1';

export interface ImpactReportState {
  tableId: string | undefined;
  forceTableReload: boolean;
  categories: ImpactReportCategory[] | undefined;
  startDate: string | undefined;
  endDate: string | undefined;
  layoutDefinition: ImpactReportDashboardContent[];
  layouts: ImpactReportLayout[];
  selectedLayoutId: string | undefined;
  aggregationColumn: string | undefined;
  biggestImpact: number;
  useCaseCount: number;
  overallImpact: number;
  filteredImpact: number;
  displayEditModal: boolean;
  showAssetPath: boolean;
  showSite: boolean;
  showLocation: boolean;
  showImpact: boolean;
  reviewedReportsOnly: boolean;
  updateNominations: boolean;
  dateRangeSelection: DateRangeSelection;
  kpiColumns: ColumnDefinitionOutputV1[];
  expectedAnnualKpiColumns: ColumnDefinitionOutputV1[];
  expectedAnnualKpiVisibility: Record<string, boolean>;
  largestKPIImpacts: ImpactTotals;
  filteredTotals: ImpactTotals;
  absoluteTotals: ImpactTotals;
  tableData: ImpactReportUseCase[];
  filteredLocation: string | undefined;
  editingTileId: string | undefined;
  columnConfiguration: ImpactReportColumnConfiguration;
  impactByLocation: ImpactReportUseCase[];
  mapZoom: MapZoomSettings | undefined;
  filterModel: ImpactReportFilterModel;
}

export interface ImpactReportActions {
  setTableId: (tableId: string | undefined) => void;
  setCategories: (categories: ImpactReportCategory[] | undefined) => void;
  setLayoutDefinition: (layoutDefinition: ImpactReportDashboardContent[]) => void;
  removeContent: (i: string) => void;
  setForceTableReload: (forceTableReload: boolean) => void;
  setAggregationColumn: (aggregationColumn: string | undefined) => void;
  setBiggestImpact: (biggestImpact: number) => void;
  setUseCaseCount: (useCaseCount: number) => void;
  setOverallImpact: (overallImpact: number) => void;
  setFilteredImpact: (filteredImpact: number) => void;
  setDisplayEditModal: (displayEditModal: boolean) => void;
  setShowAssetPath: (showAssetPath: boolean) => void;
  setShowSite: (showSite: boolean) => void;
  setShowLocation: (showLocation: boolean) => void;
  setShowImpact: (showImpact: boolean) => void;
  setReviewedReportsOnly: (reviewedReportsOnly: boolean) => void;
  setLayouts: (layouts: ImpactReportLayout[]) => void;
  setSelectedLayoutId: (selectedLayoutId: string | undefined) => void;
  setUpdateNominations: (updateNominations: boolean) => void;
  /** Selects a relative or calendar range together with the dates it resolved to, so the two never disagree. */
  setDateRangePreset: (preset: DateRangePreset, startDate: string, endDate: string) => void;
  /** Selects fixed dates. */
  setFixedDateRange: (startDate: string, endDate: string) => void;
  setTableData: (tableData: ImpactReportUseCase[]) => void;
  setImpactByLocation: (tableData: ImpactReportUseCase[]) => void;
  setKpiColumns: (kpiColumns: ColumnDefinitionOutputV1[]) => void;
  setExpectedAnnualKpiColumns: (expectedAnnualKpiColumns: ColumnDefinitionOutputV1[]) => void;
  setExpectedAnnualKpiVisibility: (expectedAnnualKpiVisibility: Record<string, boolean>) => void;
  setAbsoluteTotals: (absoluteTotals: ImpactTotals) => void;
  setFilteredTotals: (filteredTotals: ImpactTotals) => void;
  setLargestKpiImpacts: (largestKPIImpacts: ImpactTotals) => void;
  setFilteredLocation: (filteredLocation: string | undefined) => void;
  setEditingTileId: (editingTileId: string | undefined) => void;
  setColumnConfiguration: (columnConfiguration: ImpactReportColumnConfiguration) => void;
  setMapZoom: (mapZoom: MapZoomSettings | undefined) => void;
  setFilterModel: (filterModel: ImpactReportFilterModel) => void;
  reset: () => void;
}

export type ImpactReportDehydratedState = Pick<ImpactReportState, 'dateRangeSelection'> &
  Partial<Pick<ImpactReportState, 'startDate' | 'endDate'>>;

const initialState: ImpactReportState = Object.freeze({
  tableId: undefined,
  categories: undefined,
  startDate: undefined,
  endDate: undefined,
  forceTableReload: false,
  layoutDefinition: [],
  layouts: [],
  selectedLayoutId: undefined,
  aggregationColumn: undefined,
  biggestImpact: 0,
  useCaseCount: 0,
  overallImpact: 0,
  filteredImpact: 0,
  displayEditModal: false,
  showAssetPath: true,
  showSite: false,
  showLocation: true,
  updateNominations: false, // to make sure they are fetched initially
  dateRangeSelection: DEFAULT_IMPACT_REPORT_DATE_SELECTION,
  showImpact: true,
  kpiColumns: [],
  expectedAnnualKpiColumns: [],
  expectedAnnualKpiVisibility: {},
  absoluteTotals: {},
  filteredTotals: {},
  largestKPIImpacts: {},
  tableData: [],
  filteredLocation: undefined,
  editingTileId: undefined,
  columnConfiguration: {},
  impactByLocation: [],
  mapZoom: undefined,
  reviewedReportsOnly: false,
  filterModel: null,
});

const impactReportActionsFactory: ActionsFactory<ImpactReportState, ImpactReportActions> = (set, _get, resetState) => ({
  setTableId: (tableId) =>
    set((draft) => {
      draft.tableId = tableId;
    }),
  setCategories: (categories) =>
    set((draft) => {
      draft.categories = categories;
    }),
  setLayoutDefinition: (layoutDefinition) =>
    set((draft) => {
      draft.layoutDefinition = layoutDefinition;
    }),
  removeContent: (i) =>
    set((draft) => {
      draft.layoutDefinition = draft.layoutDefinition.filter((content) => content.i !== i);
    }),
  setForceTableReload: (forceTableReload) =>
    set((draft) => {
      draft.forceTableReload = forceTableReload;
    }),
  setAggregationColumn: (aggregationColumn) =>
    set((draft) => {
      draft.aggregationColumn = aggregationColumn;
    }),
  setBiggestImpact: (biggestImpact) =>
    set((draft) => {
      draft.biggestImpact = biggestImpact;
    }),
  setUseCaseCount: (useCaseCount) =>
    set((draft) => {
      draft.useCaseCount = useCaseCount;
    }),
  setOverallImpact: (overallImpact) =>
    set((draft) => {
      draft.overallImpact = overallImpact;
    }),
  setFilteredImpact: (filteredImpact) =>
    set((draft) => {
      draft.filteredImpact = filteredImpact;
    }),
  setDisplayEditModal: (displayEditModal) =>
    set((draft) => {
      draft.displayEditModal = displayEditModal;
    }),
  setShowAssetPath: (showAssetPath) =>
    set((draft) => {
      draft.showAssetPath = showAssetPath;
    }),
  setShowSite: (showSite) =>
    set((draft) => {
      draft.showSite = showSite;
    }),
  setShowLocation: (showLocation) =>
    set((draft) => {
      draft.showLocation = showLocation;
    }),
  setShowImpact: (showImpact) =>
    set((draft) => {
      draft.showImpact = showImpact;
    }),
  setReviewedReportsOnly: (reviewedReportsOnly: boolean) =>
    set((draft: ImpactReportState) => {
      draft.reviewedReportsOnly = reviewedReportsOnly;
    }),
  setLayouts: (layouts) =>
    set((draft) => {
      draft.layouts = layouts;
    }),
  setSelectedLayoutId: (selectedLayoutId) =>
    set((draft) => {
      draft.selectedLayoutId = selectedLayoutId;
    }),
  setUpdateNominations: (updateNominations) =>
    set((draft) => {
      draft.updateNominations = updateNominations;
    }),
  setDateRangePreset: (preset, startDate, endDate) =>
    set((draft) => {
      draft.dateRangeSelection = preset;
      draft.startDate = startDate;
      draft.endDate = endDate;
    }),
  setFixedDateRange: (startDate, endDate) =>
    set((draft) => {
      draft.dateRangeSelection = { kind: 'fixed' };
      draft.startDate = startDate;
      draft.endDate = endDate;
    }),
  setTableData: (tableData) =>
    set((draft) => {
      draft.tableData = tableData;
    }),
  setImpactByLocation: (impactByLocation) =>
    set((draft) => {
      draft.impactByLocation = impactByLocation;
    }),
  setKpiColumns: (kpiColumns) =>
    set((draft) => {
      draft.kpiColumns = kpiColumns;
    }),
  setExpectedAnnualKpiColumns: (expectedAnnualKpiColumns) =>
    set((draft) => {
      draft.expectedAnnualKpiColumns = expectedAnnualKpiColumns;
    }),
  setExpectedAnnualKpiVisibility: (expectedAnnualKpiVisibility) =>
    set((draft) => {
      draft.expectedAnnualKpiVisibility = expectedAnnualKpiVisibility;
    }),
  setAbsoluteTotals: (absoluteTotals) =>
    set((draft) => {
      draft.absoluteTotals = absoluteTotals;
    }),
  setFilteredTotals: (filteredTotals) =>
    set((draft) => {
      draft.filteredTotals = filteredTotals;
    }),
  setLargestKpiImpacts: (largestKPIImpacts) =>
    set((draft) => {
      draft.largestKPIImpacts = largestKPIImpacts;
    }),
  setFilteredLocation: (filteredLocation) =>
    set((draft) => {
      draft.filteredLocation = filteredLocation;
    }),
  setEditingTileId: (editingTileId) =>
    set((draft) => {
      draft.editingTileId = editingTileId;
    }),
  setColumnConfiguration: (columnConfiguration) =>
    set((draft) => {
      draft.columnConfiguration = columnConfiguration;
    }),
  setMapZoom: (mapZoom) =>
    set((draft) => {
      draft.mapZoom = mapZoom;
    }),
  setFilterModel: (filterModel) =>
    set((draft) => {
      draft.filterModel = filterModel;
    }),
  reset: resetState,
});

const [ImpactReportStoreProvider, useImpactReportStoreInstance, useImpactReportSelector] = createStore(
  'ImpactReport',
  initialState,
  impactReportActionsFactory,
);

export { ImpactReportStoreProvider };

/**
 * Creates a new store instance for tests that must assert exact store state or when
 * porting Flux store tests that call actions imperatively.
 *
 * Prefer `<TestStoreProvider>` or `<*StoreProvider>` in tests and validate behavior
 * through UI interactions + DOM assertions whenever possible.
 *
 * NOTE: Only use this function in **test files**.
 *
 * @deprecated This function is ONLY for legacy migration from Flux/Baobab stores. Prefer
 * `<TestStoreProvider>` or `<*StoreProvider>` with UI-driven assertions instead.
 */
export function createTestImpactReportStore(overrides?: Partial<ImpactReportState>) {
  if (process.env.NODE_ENV !== 'test') {
    throw new Error('createTestImpactReportStore() should only be used in tests.');
  }

  return createStoreInstance({ ...initialState, ...overrides }, impactReportActionsFactory);
}

export const selectImpactReportConfiguration = createSelector(
  (state: ImpactReportState) => state.showAssetPath,
  (state: ImpactReportState) => state.showSite,
  (state: ImpactReportState) => state.showLocation,
  (state: ImpactReportState) => state.showImpact,
  (state: ImpactReportState) => state.expectedAnnualKpiVisibility,
  (showAssetPath, showSite, showLocation, showImpact, expectedAnnualKpiVisibility): ImpactReportConfiguration => ({
    showAssetPath,
    showSite,
    showLocation,
    showImpact,
    expectedAnnualKpiVisibility,
  }),
);

export const dehydrateImpactReportState = (state: ImpactReportState): ImpactReportDehydratedState => ({
  ...(state.dateRangeSelection.kind === 'fixed' ? { startDate: state.startDate, endDate: state.endDate } : {}),
  dateRangeSelection: state.dateRangeSelection,
});

type DisplayValueProperties = keyof Pick<
  ImpactReportState,
  'overallImpact' | 'biggestImpact' | 'filteredImpact' | 'useCaseCount'
>;

export const useImpactReportActions = () => useImpactReportStoreInstance().actions;
export const useImpactReportBoxDisplayValue = ({
  property,
  adjustedProperty,
  actualValue,
}: {
  property: DisplayValueProperties | string;
  adjustedProperty: string;
  actualValue?: string;
}): number | undefined =>
  useImpactReportSelector((state) => {
    const isOverall = actualValue === 'overall' || property === 'overallImpact';
    const isLargest = actualValue === 'largest' || property === 'biggestImpact';

    if (isOverall) {
      return state.filteredTotals[adjustedProperty];
    }

    if (isLargest) {
      return state.largestKPIImpacts[adjustedProperty];
    }

    if (property === 'filteredImpact') {
      return state.filteredImpact;
    }

    if (property === 'useCaseCount') {
      return state.useCaseCount;
    }

    return undefined;
  });
export const useImpactReportTableId = () => useImpactReportSelector((state) => state.tableId);
export const useImpactReportCategories = () => useImpactReportSelector((state) => state.categories);
export const useImpactReportStartDate = () => useImpactReportSelector((state) => state.startDate);
export const useImpactReportEndDate = () => useImpactReportSelector((state) => state.endDate);
export const useImpactReportLayoutDefinition = () => useImpactReportSelector((state) => state.layoutDefinition);
export const useImpactReportLayouts = () => useImpactReportSelector((state) => state.layouts);
export const useImpactReportSelectedLayoutId = () => useImpactReportSelector((state) => state.selectedLayoutId);
export const useImpactReportAggregationColumn = () => useImpactReportSelector((state) => state.aggregationColumn);
export const useImpactReportBiggestImpact = () => useImpactReportSelector((state) => state.biggestImpact);
export const useImpactReportUseCaseCount = () => useImpactReportSelector((state) => state.useCaseCount);
export const useImpactReportOverallImpact = () => useImpactReportSelector((state) => state.overallImpact);
export const useImpactReportFilteredImpact = () => useImpactReportSelector((state) => state.filteredImpact);
export const useImpactReportDisplayEditModal = () => useImpactReportSelector((state) => state.displayEditModal);
export const useImpactReportShowAssetPath = () => useImpactReportSelector((state) => state.showAssetPath);
export const useImpactReportShowSite = () => useImpactReportSelector((state) => state.showSite);
export const useImpactReportShowLocation = () => useImpactReportSelector((state) => state.showLocation);
export const useImpactReportShowImpact = () => useImpactReportSelector((state) => state.showImpact);
export const useImpactReportReviewedReportsOnly = () => useImpactReportSelector((state) => state.reviewedReportsOnly);
export const useImpactReportUpdateNominations = () => useImpactReportSelector((state) => state.updateNominations);
export const useImpactReportDateRangeSelection = () => useImpactReportSelector((state) => state.dateRangeSelection);
export const useImpactReportKpiColumns = () => useImpactReportSelector((state) => state.kpiColumns);
export const useImpactReportExpectedAnnualKpiColumns = () =>
  useImpactReportSelector((state) => state.expectedAnnualKpiColumns);
export const useImpactReportExpectedAnnualKpiVisibility = () =>
  useImpactReportSelector((state) => state.expectedAnnualKpiVisibility);
export const useImpactReportLargestKpiImpacts = () => useImpactReportSelector((state) => state.largestKPIImpacts);
export const useImpactReportFilteredTotals = () => useImpactReportSelector((state) => state.filteredTotals);
export const useImpactReportAbsoluteTotals = () => useImpactReportSelector((state) => state.absoluteTotals);
export const useImpactReportTableData = () => useImpactReportSelector((state) => state.tableData);
export const useImpactReportFilteredLocation = () => useImpactReportSelector((state) => state.filteredLocation);
export const useImpactReportEditingTileId = () => useImpactReportSelector((state) => state.editingTileId);
export const useImpactReportColumnConfiguration = () => useImpactReportSelector((state) => state.columnConfiguration);
export const useImpactReportImpactByLocation = () => useImpactReportSelector((state) => state.impactByLocation);
export const useImpactReportMapZoom = () => useImpactReportSelector((state) => state.mapZoom);
export const useImpactReportFilterModel = () => useImpactReportSelector((state) => state.filterModel);
export const useImpactReportForceTableReload = () => useImpactReportSelector((state) => state.forceTableReload);
export const useImpactReportConfiguration = () => useImpactReportSelector(selectImpactReportConfiguration);

export const usePersistImpactReportStore = () => {
  const register = useRegisterPersistedStore();
  const store = useImpactReportStoreInstance();

  useEffect(() => {
    return register({
      storeName: 'sqImpactReportStore',
      persistenceLevel: 'WORKBENCH',
      store,
      dehydrate: dehydrateImpactReportState,
    });
  }, [register, store]);
};
